import { useDispatch } from "react-redux"
import { type QueryFunction, useQuery } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { makeUniverseFilter } from "gatherers/helpers"
import { type ContributorPositionSet } from "gatherers/contributors/helpers/types"
import { IsoDate } from "helpers/types/dates"

const SECURITY_SEARCH_COUNT_QUERY = `query ($modelId: String!, $filter: [SecuritySearchFilter!]!, $on: Date!, $skip: Int!, $sort: [SecuritySearchSort], $currency: Currency) {
  model (id: $modelId) {
    securitySearch (filter: $filter, on: $on, skip: $skip, sort: $sort, currency: $currency) {
      count
    }
  }
}`

interface DataResponse {
  data: {
    model: {
      securitySearch: {
        count: number
      }
    }
  }
}

interface Options {
  modelId: string
  positionSet: ContributorPositionSet
  base: ContributorPositionSet
  to: IsoDate
  currency: string
  enabled?: boolean
}

export default function useSecuritySearchCount(options: Options) {
  const account = useAccountAlias()
  const dispatch = useDispatch()
  const { modelId, positionSet, base, to, currency, enabled } = options

  const filter = makeUniverseFilter(positionSet, base)
  const variables = {
    modelId,
    filter,
    on: to,
    currency,
    skip: 0,
  }

  const queryFn: QueryFunction<DataResponse, (string | ContributorPositionSet)[]> = () => {
    return new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, SECURITY_SEARCH_COUNT_QUERY, variables, resolve, reject))
    })
  }

  const result = useQuery({
    queryKey: ["descriptors", "count", modelId, positionSet, base, to, currency],
    queryFn,
    enabled: !!filter && enabled,
    select: (data: DataResponse) => data.data.model.securitySearch.count,
  })

  return result?.data
}
